import { Theme } from '@lifeomic/chroma-react/styles';
import { Components } from '@lifeomic/chroma-react/styles/overrides/ChromaComponents';
import { alpha, lighten } from '@mui/material/styles';
import {
  primaryGradient270Deg as primaryGradient270DegStyles,
  SEA_GREEN,
} from './styles/content';
import { DARK_SIDEBAR_BACKGROUND } from './palette';

export const overrides = (theme: Theme): Components => ({
  ChromaAvatar: {
    styleOverrides: {
      root: {
        borderColor: theme.palette.common.white,
        '&:hover, &:focus': {
          borderColor: alpha(theme.palette.common.white, 0.5),
        },
      },
      clickable: {
        '&:hover': {
          borderColor: alpha(theme.palette.common.white, 0.5),
        },
      },
      defaultSrc: {
        fill: 'none',
        stroke: theme.palette.primary.main,
        strokeWidth: theme.pxToRem(5),
      },
    },
  },
  ...(theme.palette.mode === 'dark' && {
    ChromaButton: {
      styleOverrides: {
        outlined: {
          borderColor: theme.palette.blue[200],
          color: theme.palette.blue[200],
          '&:hover': {
            backgroundColor: 'transparent',
            borderColor: theme.palette.blue[100],
            color: theme.palette.blue[100],
          },
          '&:disabled': {
            borderColor: theme.palette.blue[200],
            color: theme.palette.blue[200],
          },
        },
        text: {
          color: theme.palette.blue[200],
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.blue[100],
          },
          '&:disabled': {
            backgroundColor: 'transparent',
            color: theme.palette.blue[200],
            opacity: 0.44,
          },
        },
        containedInverse: {
          backgroundColor: alpha(theme.palette.common.white, 0.14),
          color: theme.palette.common.white,
          border: `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
          '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.22),
            color: theme.palette.common.white,
            opacity: 1,
          },
          '&:focus': {
            outline: 'none',
          },
          '&:disabled, &[disabled]': {
            backgroundColor: alpha(theme.palette.common.white, 0.08),
            color: alpha(theme.palette.common.white, 0.45),
            opacity: 1,
          },
        },
      },
    },
    ChromaPaper: {
      styleOverrides: {
        root: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.45)}`,
        },
      },
    },
    ChromaSmallTile: {
      styleOverrides: {
        root: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.45)}`,
        },
        cursorPointer: {
          '&:hover, &:focus': {
            boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.55)}`,
            '& > * > p': {
              color: theme.palette.blue[200],
            },
          },
        },
      },
    },
    ChromaSmallTileContent: {
      styleOverrides: {
        text: {
          color: theme.palette.text.primary,
        },
      },
    },
    ChromaSmallTileFooter: {
      styleOverrides: {
        text: {
          color: theme.palette.text.secondary,
        },
      },
    },
    ChromaModal: {
      styleOverrides: {
        content: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
        modalChildrenContainer: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
        modalChildrenContainerFullScreen: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
        contentFullScreen: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
        modalHeaderFullScreen: {
          backgroundColor: theme.palette.background.paper,
        },
      },
    },
    ChromaIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: theme.palette.blue[200],
          },
          '&:focus': {
            color: theme.palette.blue[200],
          },
        },
        primary: {
          color: theme.palette.blue[200],
          '&:hover, &:focus': {
            color: theme.palette.blue[100],
          },
          '&:disabled': {
            color: theme.palette.blue[200],
            opacity: 0.44,
          },
        },
      },
    },
    ChromaButtonLink: {
      styleOverrides: {
        outlined: {
          borderColor: theme.palette.blue[200],
          color: theme.palette.blue[200],
          '&:hover, &:focus': {
            backgroundColor: 'transparent',
            borderColor: theme.palette.blue[100],
            color: theme.palette.blue[100],
          },
        },
        text: {
          color: theme.palette.blue[200],
          '&:hover, &:focus': {
            backgroundColor: 'transparent',
            color: theme.palette.blue[100],
          },
        },
      },
    },
    ChromaIconButtonLink: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: theme.palette.blue[200],
          },
          '&:focus': {
            color: theme.palette.blue[200],
          },
        },
      },
    },
    ChromaPopover: {
      styleOverrides: {
        root: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      },
    },
    ChromaRadioGroupMinimal: {
      styleOverrides: {
        legend: {
          color: theme.palette.text.primary,
        },
        radios: {
          background: alpha(theme.palette.common.white, 0.08),
          '& input:not(:checked) + div label > p': {
            color: theme.palette.text.primary,
          },
          '& input:checked + div label > p': {
            color: theme.palette.common.white,
          },
        },
      },
    },
    ChromaSelect: {
      styleOverrides: {
        label: {
          color: theme.palette.text.primary,
        },
        button: {
          backgroundColor: alpha(theme.palette.common.white, 0.08),
          color: theme.palette.text.primary,
        },
        popover: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      },
    },
    ChromaSelectOption: {
      styleOverrides: {
        subtitle: {
          color: theme.palette.text.hint,
        },
        checked: {
          '&::before': {
            background: theme.palette.blue[200],
          },
          '& *': {
            color: theme.palette.blue[200],
          },
        },
        obviousHover: {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
    },
    ChromaRoverOption: {
      styleOverrides: {
        option: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          '&:hover, &:focus': {
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
    },
    ChromaMenu: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.45)}`,
        },
        title: {
          color: theme.palette.text.primary,
        },
      },
    },
    ChromaMenuItem: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          '&:hover, &:focus': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
          },
        },
        text: {
          color: 'inherit',
        },
        secondaryText: {
          color: theme.palette.text.secondary,
        },
      },
    },
    ChromaRadioGroup: {
      styleOverrides: {
        legend: {
          color: theme.palette.text.primary,
        },
      },
    },
    ChromaLink: {
      styleOverrides: {
        root: {
          color: theme.palette.blue[200],
          '&:hover': {
            color: theme.palette.blue[100],
          },
        },
      },
    },
    ChromaAlert: {
      styleOverrides: {
        info: {
          backgroundColor: alpha(theme.palette.blue.main, 0.15),
          color: theme.palette.text.primary,
        },
      },
    },
    ChromaBreadcrumb: {
      styleOverrides: {
        root: {
          '& + li:before': {
            borderRightColor: theme.palette.text.secondary,
          },
        },
        link: {
          color: theme.palette.text.secondary,
          '&:hover': {
            color: theme.palette.blue[200],
          },
        },
      },
    },
    ChromaExpansionPanel: {
      styleOverrides: {
        header: {
          background: theme.palette.background.paper,
        },
        button: {
          background: theme.palette.background.paper,
          '&:hover > p, &:focus > p': {
            color: theme.palette.text.primary,
          },
        },
        title: {
          color: theme.palette.text.primary,
        },
        inner: {
          background: theme.palette.background.paper,
        },
        iconButton: {
          color: theme.palette.blue[200],
        },
      },
    },
    ChromaTableHeaderCell: {
      styleOverrides: {
        root: {
          background: theme.palette.background.default,
          color: theme.palette.text.primary,
        },
        clickable: {
          '&:hover': {
            color: theme.palette.text.secondary,
            '& > *': {
              color: theme.palette.text.secondary,
            },
          },
        },
        icon: {
          color: theme.palette.text.secondary,
        },
        isSticky: {
          background: theme.palette.background.default,
        },
      },
    },
    ChromaTab: {
      styleOverrides: {
        root: {
          color: theme.palette.text.secondary,
          '&:hover': {
            color: theme.palette.text.primary,
          },
          '&[aria-selected="true"]': {
            color: theme.palette.common.white,
          },
          '& svg, & svg *': {
            color: 'currentColor',
            stroke: 'currentColor',
          },
        },
        pill: {
          color: theme.palette.text.secondary,
          '&:hover': {
            color: theme.palette.text.primary,
          },
          '&[aria-selected="true"]': {
            color: theme.palette.common.white,
          },
        },
      },
    },
    ChromaTabList: {
      styleOverrides: {
        pill: {
          background: alpha(theme.palette.common.white, 0.08),
          borderBottom: 'none',
        },
      },
    },
    ChromaSearchField: {
      styleOverrides: {
        input: {
          backgroundColor: alpha(theme.palette.common.white, 0.08),
          color: theme.palette.text.primary,
          border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
          '&::-webkit-input-placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
          },
          '&::-moz-placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
          },
          '&:-ms-input-placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
          },
          '&:-moz-placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
          },
          '&:focus': {
            backgroundColor: alpha(theme.palette.common.white, 0.12),
            border: `1px solid ${theme.palette.divider}`,
          },
        },
      },
    },
    ChromaTextField: {
      styleOverrides: {
        label: {
          color: theme.palette.text.primary,
        },
        input: {
          backgroundColor: alpha(theme.palette.common.white, 0.08),
          border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
          boxShadow: 'none',
          '&:focus': {
            backgroundColor: alpha(theme.palette.common.white, 0.12),
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
          },
          '&::-webkit-input-placeholder': {
            color: theme.palette.text.hint,
          },
          '&::-moz-placeholder': {
            color: theme.palette.text.hint,
          },
          '&:-ms-input-placeholder': {
            color: theme.palette.text.hint,
          },
          '&:-moz-placeholder': {
            color: theme.palette.text.hint,
          },
        },
      },
    },
  }),
  ChromaButtonFloat: {
    styleOverrides: {
      root: {
        ...primaryGradient270DegStyles,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          backgroundImage: 'none',
        },
      },
    },
  },
  ChromaDotLoader: {
    styleOverrides: {
      root: {
        '& > circle': {
          '&:nth-child(1)': {
            fill: theme.palette.blue.main,
          },
          '&:nth-child(2)': {
            fill: '#3170a8',
          },
          '&:nth-child(3)': {
            fill: '#437b79',
          },
          '&:nth-child(4)': {
            fill: SEA_GREEN,
          },
        },
      },
    },
  },
  ChromaHeader: {
    styleOverrides: {
      root: {
        ...primaryGradient270DegStyles,
      },
      logo: {
        marginRight: theme.spacing(0.5),
      },
      centerLogo: {
        [theme.breakpoints.down('lg')]: {
          margin: theme.spacing(0, 1),
          position: 'unset',
          transform: 'unset',
        },
      },
    },
  },
  ChromaIconButtonFloat: {
    styleOverrides: {
      root: {
        ...primaryGradient270DegStyles,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          backgroundImage: 'none',
        },
      },
    },
  },
  ChromaLayoutManager: {
    styleOverrides: {
      header: {
        height: 'var(--app-header-height, auto)',
        minHeight: 'var(--app-header-min-height, auto)',
        maxHeight: 'var(--app-header-max-height, none)',
        overflow: 'var(--app-header-overflow, visible)',
      },
      main: {
        ...(theme.palette.mode === 'dark' && {
          backgroundColor: theme.palette.background.default,
        }),
      },
      sidebar: {
        boxShadow: `0px 4px 24px ${theme.hexToRgba(
          theme.palette.common.black,
          theme.palette.mode === 'dark' ? 0.35 : 0.05,
        )}`,
        width: 'var(--app-sidebar-width, 16.625rem)',
        ...(theme.palette.mode === 'dark' && {
          backgroundColor: DARK_SIDEBAR_BACKGROUND,
        }),
      },
      sidebarCollapsed: {
        width: 'var(--app-sidebar-collapsed-width, 3.75rem)',
      },
    },
  },
  ChromaLinearProgress: {
    styleOverrides: {
      root: {
        '--linear-progress-bar-color':
          primaryGradient270DegStyles.backgroundImage,
      },
    },
  },
  ChromaPageHeader: {
    styleOverrides: {
      root: {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.95)
            : alpha(theme.palette.graphite[100], 0.75),
        backgroundImage: 'none',
      },
    },
  },
  ChromaPill: {
    styleOverrides: {
      highlight: {
        ...primaryGradient270DegStyles,
        borderBottomColor: theme.palette.primary.dark,
        borderRightColor: theme.palette.primary.dark,
      },
    },
  },
  ChromaPageLayout: {
    styleOverrides: {
      backgroundCover: {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : 'transparent',
        backgroundImage: 'none',
      },
      content: {
        ...(theme.palette.mode === 'dark' && {
          backgroundColor: theme.palette.background.default,
        }),
      },
      layoutContainer: {
        ...(theme.palette.mode === 'dark' && {
          backgroundColor: theme.palette.background.default,
        }),
      },
      root: {
        ...(theme.palette.mode === 'dark' && {
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
  },
  ChromaPrimaryNavigation: {
    styleOverrides: {
      root: {
        '--link-color': theme.palette.text.primary,
        '--link-hover': theme.palette.blue.main,
        '--link-active': theme.palette.blue.main,
        '--link-active-bg':
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.08)
            : alpha(theme.palette.graphite.main, 0.15),
        backgroundColor:
          theme.palette.mode === 'dark'
            ? DARK_SIDEBAR_BACKGROUND
            : theme.palette.common.white,
        paddingTop: 0,
      },
      footToggle: {
        color: theme.palette.text.primary,
        '& svg *': {
          strokeWidth: theme.pxToRem(1.5),
        },
        '&:hover': {
          color: 'var(--link-hover)',
          '& svg': {
            color: 'var(--link-hover)',
          },
        },
      },
      navItems: {
        paddingTop: theme.pxToRem(12),
      },
    },
  },
  ChromaPrimaryNavigationItem: {
    styleOverrides: {
      link: {
        '&:hover': {
          '& > div > svg': {
            color: 'var(--link-hover)',
          },
        },
      },
      linkActive: {
        color: theme.palette.text.primary,
        '&:hover': {
          opacity: 1,
        },
      },
      icon: {
        '& svg *': {
          strokeWidth: theme.pxToRem(1.5),
        },
      },
    },
  },
  ChromaPrimaryNavigationExpansionItem: {
    styleOverrides: {
      link: {
        '&:hover': {
          '& > div > svg': {
            color: 'var(--link-hover)',
          },
        },
      },
      linkActive: {
        color: theme.palette.text.primary,
        '&:hover': {
          opacity: 1,
        },
      },
      icon: {
        '& svg *': {
          strokeWidth: theme.pxToRem(1.5),
        },
      },
    },
  },
  ChromaSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.charcoal[700]
            : theme.palette.graphite[50],
      },
      shineAnimation: {
        '&::after': {
          background: `linear-gradient(90deg, transparent, ${lighten(
            theme.palette.mode === 'dark'
              ? theme.palette.charcoal[700]
              : theme.palette.graphite[50],
            0.75,
          )}, transparent)`,
        },
      },
    },
  },
  ChromaTableModule: {
    styleOverrides: {
      tableModuleActions: {
        ...primaryGradient270DegStyles,
        '&:empty': {
          display: 'none',
        },
      },
      ...(theme.palette.mode === 'dark' && {
        root: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
        tableRow: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
        tableRowCell: {
          color: theme.palette.text.primary,
        },
        tableDataRow: {
          '&:hover': {
            background: theme.palette.charcoal[700],
          },
          '&:focus.focus-visible, &:focus-within': {
            background: theme.palette.charcoal[700],
          },
        },
        isSticky: {
          background: theme.palette.background.paper,
        },
      }),
    },
  },
  ChromaText: {
    styleOverrides: {
      root: {
        color: theme.palette.text.primary,
      },
      headline: {
        lineHeight: 1.65,
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        border: `1px solid ${theme.palette.divider}`,
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiCheckbox: {
    defaultProps: {
      color: 'primary',
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      checked: {},
      colorPrimary: {
        '&$checked': {
          color: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
      colorSecondary: {
        '&$checked': {
          color: theme.palette.primary.main,
        },
      },
    },
  },
  MuiCircularProgress: {
    styleOverrides: {
      colorPrimary: {
        color: theme.palette.primary.main,
      },
      colorSecondary: {
        color: theme.palette.secondary.main,
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        color: theme.palette.common.white,
        backgroundImage: `linear-gradient(135deg, #0abff1 0%, #0096e1 22%, rgb(166, 66, 254) 92%)`,
        '&:hover': {
          backgroundImage: `linear-gradient(135deg, #0abff1 0%, #0096e1 7.5%, rgb(166, 66, 254) 92%)`,
        },
        '&:focus': {
          outline: 'none',
        },
        '&$focusVisible': {
          backgroundColor: theme.palette.primary.main,
          backgroundImage: 'none',
        },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      focused: {},
      root: {
        '&$focused': {
          color: theme.palette.primary.main,
        },
      },
    },
  },
  MuiInput: {
    styleOverrides: {
      underline: {
        '&:after': {
          borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      barColorPrimary: {
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : theme.palette.common.white,
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      colorPrimary: {
        '&$checked': {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
      colorSecondary: {
        '&$checked': {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      checked: {},
      colorPrimary: {
        '&$checked': {
          color: theme.palette.primary.main,
        },
      },
      colorSecondary: {
        '&$checked': {
          color: theme.palette.primary.main,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `solid 1px ${theme.palette.divider}`,
      },
      head: {
        fontSize: theme.typography.body1.fontSize,
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.text.primary,
        textTransform: 'uppercase',
      },
      body: {
        fontSize: theme.typography.subtitle1.fontSize,
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&$selected': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.charcoal[700]
              : theme.palette.grey[300],
        },
        '&$hover:hover': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.charcoal[700]
              : theme.palette.grey[300],
        },
      },
    },
  },
});
